// =============================================================================
// Dashboard Auth & Navigation
// Extracted from make_album/dashboard.html inline JS (lines 1289-1395)
// =============================================================================

import type { User } from './dashboard-types';

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

let currentView: string = 'works';

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------

function updateAvatarUI(user: User): void {
  const avatar = user.avatar_url || user.avatar;
  const target = document.getElementById('userAvatar');
  if (!target) return;
  if (avatar) {
    target.innerHTML = '';
    const img = document.createElement('img');
    img.src = avatar;
    img.style.cssText = 'width:100%;height:100%;object-fit:cover;border-radius:50%;';
    target.appendChild(img);
  } else {
    const initial = (user.nickname || user.username || '?').charAt(0).toUpperCase();
    target.textContent = initial;
  }
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

function checkAuth(): boolean {
  // Render cached user immediately if available
  const user: User | null = JSON.parse(localStorage.getItem('currentUser') || 'null');
  if (user) {
    updateAvatarUI(user);
  }
  // Always verify with server via cookie-based auth.
  // Don't redirect until /api/auth/me fails — the user may have a valid
  // HttpOnly cookie even if localStorage is empty.
  fetch('/api/auth/me', { credentials: 'include' })
    .then(function (resp) {
      if (!resp.ok) {
        localStorage.removeItem('currentUser');
        window.location.href = 'login.html';
        return;
      }
      return resp.json().then(function (data: { user: User }) {
        localStorage.setItem('currentUser', JSON.stringify(data.user));
        updateAvatarUI(data.user);
      });
    })
    .catch(function () {
      // Network error — if we have cached user, keep using it
      if (!JSON.parse(localStorage.getItem('currentUser') || 'null')) {
        window.location.href = 'login.html';
      }
    });
  return true;
}

// ---------------------------------------------------------------------------
// Clear local account data
// ---------------------------------------------------------------------------

function clearLocalAccountData(
  userId: string | number,
  includeOwnedData: boolean,
): void {
  localStorage.removeItem('authToken');
  localStorage.removeItem('currentUser');
  if (userId) {
    localStorage.removeItem('userAddresses_' + userId);
    localStorage.removeItem('registeredUsers_' + userId);
  }
  if (!includeOwnedData) return;
  localStorage.removeItem('userAlbums');
  localStorage.removeItem('userOrders');
  if (typeof indexedDB !== 'undefined') {
    indexedDB.deleteDatabase('AlbumEditorDB');
  }
}

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

/**
 * Call once on page load. Verifies auth and sets up the pageshow listener
 * to re-check on bfcache restore.
 */
function init(): void {
  if (!checkAuth()) throw new Error('not logged in');
  window.addEventListener('pageshow', function (e: PageTransitionEvent) {
    if (e.persisted) checkAuth();
  });
}

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------

/**
 * View renderer registry. The main module registers each view's render
 * function via `registerView()`. `renderView` dispatches to the matching
 * entry.
 */
const viewRenderers: Map<string, (container: HTMLElement) => void> = new Map();

function registerView(name: string, renderer: (container: HTMLElement) => void): void {
  viewRenderers.set(name, renderer);
}

function renderView(view: string): void {
  const container = document.getElementById('mainContent');
  if (!container) return;
  const renderer = viewRenderers.get(view);
  if (renderer) {
    renderer(container);
  }
}

/**
 * Sets up sidebar menu click handlers, user dropdown toggle, and logout
 * button. Must be called after the DOM is ready.
 */
function initNavigation(): void {
  // Menu items
  document.querySelectorAll('.menu-item').forEach(function (item) {
    item.addEventListener('click', function () {
      document.querySelectorAll('.menu-item').forEach(function (m) {
        m.classList.remove('active');
      });
      this.classList.add('active');
      currentView = (this as HTMLElement).getAttribute('data-view') || 'works';
      renderView(currentView);
    });
  });

  // User dropdown
  const userArea = document.getElementById('userArea');
  const userDropdown = document.getElementById('userDropdown');
  if (userArea && userDropdown) {
    userArea.addEventListener('click', function (e) {
      e.stopPropagation();
      userDropdown.classList.toggle('show');
    });
    document.addEventListener('click', function () {
      userDropdown.classList.remove('show');
    });
  }

  // Logout
  const logoutBtn = document.getElementById('logoutBtn');
  if (logoutBtn) {
    logoutBtn.addEventListener('click', function () {
      const user = JSON.parse(localStorage.getItem('currentUser') || '{}') as Partial<User>;
      const userId = user.id || 'anonymous';
      fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
        .finally(function () {
          clearLocalAccountData(userId, false);
          window.location.href = 'login.html';
        });
    });
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export { init, initNavigation, updateAvatarUI, checkAuth, clearLocalAccountData, renderView, registerView };
export { currentView };
export { setCurrentView };

function setCurrentView(v: string): void {
  currentView = v;
}
